// ────────────────────────────────────────────────────────────────────
// navItems — the single list of routed destinations the workbench shell
// exposes. `visibleNav` decides what exists and who may see it; every nav
// surface derives from it, so no two surfaces can disagree about a
// destination or about its permission.
//
// ── 2026-09-01, Task 4: the header becomes the design's seven ────────
//     QUEUE   ASSETS   FLOW MAP   DASHBOARD   ADMIN   ALERTS   FIELD APP
// (spec: 2026-09-01-the-queue-design.md, "The navigation changes").
//
// Three of the eight old slots are gone and one is new:
//
//   TODAY -> QUEUE. "A better name: it is not a day's tray, and it never
//     was." The slot points at /queue — the staff pipeline, one list split
//     by whose move it is.
//
//   FILES is gone AS A SLOT. "Receiving files opens a job at Intake, and
//     stage 2 maps it" — the file room is stages 1–2 of the queue rather
//     than a room of its own. Gone as a slot is not gone as a place: the
//     destination rides in the promoted row (see promotedNav) and all three
//     of its lenses still resolve. A slot is a claim about the model; the
//     row is about reach.
//
//   MAP -> FLOW MAP, and it has no screen. The handoff's MAP slot held
//     `atlas` only because no flow map existed; one is drawn now (the right
//     rail's FLOW), so the geographic atlas gives the slot back.
//
//   ASSETS gains the destination MAP used to hold. Decided 2026-09-01:
//     "`atlas` becomes a view mode on ASSETS, not a nav slot" — it is a
//     spatial index of assets, which `AtlasScreen.tsx:2` calls "Level 1 of
//     the customer portal drill-down", a way IN rather than a destination.
//     The map is the only view mode ASSETS has today, and the national
//     concession overlay rides inside it. This is the reason the old
//     `ASSET -> null` comment gave for refusing /atlas ("a second button
//     onto the MAP screen") no longer applies: there is no MAP screen slot
//     for it to duplicate.
//
// ── MenuBar retired with this change, and stranded nothing ───────────
// It was the last surface offering a destination no slot covers. Every leaf
// it carried was enumerated before it went:
//
//   /today       -> promoted row      /inbox   -> ALERTS slot
//   /atlas       -> ASSETS slot       /settings-> ADMIN slot (and UserMenu)
//   /files/queue -> promoted row      /reports -> promoted row (and UserMenu)
//   MODS         -> not a route at all; a dock window, reachable from the
//                   DockBar pill that ModsDockMounter registers.
//
// /reports is the one that had blocked this since 2026-08-27. It had a home
// already — the account menu — and it gains a first-class one here rather
// than being dropped to match a drawing. ARCH still does not exist; when it
// does, /reports moves into it and leaves this list.
//
// /today is NOT superseded by /queue and does not retire with TODAY the
// slot. GET /api/queue is `require_role(*STAFF_ROLES)`, so a customer has no
// pipeline at all and /today is their only work surface; and even for staff
// TODAY carries what the queue does not — assets nearing due with no job
// against them, unanswered conversations, รอตรวจ, and the only free-form way
// to open a job. Retiring it is its own piece of work with its own
// destinations to hand over, not a side effect of renaming a slot.
// ────────────────────────────────────────────────────────────────────
import type { LucideIcon } from "lucide-react";
import { LayoutDashboard, ClipboardList, Inbox, Map, FolderOpen, FileText, Settings,
         LayoutList, Boxes, Bell, Radio, Workflow } from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  staffOnly?: boolean;
  /** Also rendered in the nav row, after the divider, when no slot names it.
   *  This is the reach guarantee that lets a slot be removed from the model
   *  without a destination going dark — see promotedNav. */
  quick?: boolean;
}

export const NAV: NavItem[] = [
  // QUEUE — the staff pipeline. Staff-only because its endpoint is:
  // routers/queue.py gates GET /api/queue on require_role(*STAFF_ROLES), and
  // a slot offered to someone the route refuses is the ALERTS defect again.
  { to: "/queue",       label: "คิวงาน",        icon: LayoutList,    staffOnly: true },
  { to: "/today",       label: "ถาดงานวันนี้", icon: ClipboardList, quick: true },
  { to: "/inbox",       label: "Inbox",         icon: Inbox,         staffOnly: true },
  // The spatial index of assets — the ASSETS slot's one view mode today.
  { to: "/atlas",       label: "Atlas",         icon: Map },
  // ONE destination for files, three lenses inside it
  // (components/files/FileTabs.tsx). No slot names it any more; `quick` is
  // what keeps it one click away.
  { to: "/files/queue", label: "ไฟล์",           icon: FolderOpen,    quick: true },
  { to: "/reports",     label: "รายงาน",         icon: FileText,      quick: true },
  { to: "/settings",    label: "ตั้งค่า",         icon: Settings },
];

// (A `customerOnly` mirror lived here for about an hour on 2026-08-04, so
// /intake could carry a different label per role while staff and customers
// landed on different surfaces behind it. Collapsing those surfaces into one
// destination with tabs removed the need, and a flag with no user is a trap —
// the next person would have had to work out whether it was load-bearing.)
export function visibleNav(isStaff: boolean): NavItem[] {
  return NAV.filter((item) => isStaff || !item.staffOnly);
}
export function visibleQuickRow(isStaff: boolean): NavItem[] {
  return visibleNav(isStaff).filter((item) => item.quick);
}

// ────────────────────────────────────────────────────────────────────
// The slot vocabulary (2026-09-01 design: the queue).
//
// It lives here, beside NAV, on purpose. It used to live in AppShell.tsx and
// restate two facts NAV already owned — the destination and the staff gate —
// which is how ALERTS came to be offered to customers whose /inbox redirects
// them away on arrival. A slot now NAMES a destination; NAV decides whether it
// exists and who may see it.
// ────────────────────────────────────────────────────────────────────
export interface NavSlot {
  /** Stable identifier — used for the test id and as the React key. Never
   *  rendered, so it carries no spaces and survives a rewording. */
  key: string;
  /** The word the header prints, in the design's own spelling. */
  text: string;
  /** Thai tooltip / title attribute. */
  label: string;
  /** null while no screen exists — the slot is declared but not rendered. */
  to: string | null;
  icon: LucideIcon;
}

/** The design's seven.
 *
 *  `to: null` is a declaration without a destination: the slot is named here so
 *  the model stays whole and the next person can see what is coming, but it does
 *  not render. A dead button that answers a click with nothing is worse than an
 *  absent one — giving a slot a screen must be the deliberate act of writing its
 *  route in, both here and in NAV above. That invariant came from the
 *  eight-slot model and survives unchanged into seven.
 *
 *  Three carry no screen today:
 *    FLOW MAP  — drawn in the design's right rail; QueueRail renders FLOW as a
 *                rail, not as a screen of its own.
 *    DASHBOARD — the fleet-dashboard BACKEND exists on `feat/fleet-dashboard`,
 *                which is not this branch, and there is no UI here either way.
 *    FIELD APP — no screen anywhere in this tree.
 */
export const NAV_SLOTS: readonly NavSlot[] = [
  { key: "QUEUE",  text: "QUEUE",     label: "คิวงาน",     to: "/queue",    icon: LayoutList },
  { key: "ASSETS", text: "ASSETS",    label: "สินทรัพย์",  to: "/atlas",    icon: Boxes },
  { key: "FLOW",   text: "FLOW MAP",  label: "ผังการไหล",  to: null,        icon: Workflow },
  { key: "DASH",   text: "DASHBOARD", label: "ภาพรวม",     to: null,        icon: LayoutDashboard },
  { key: "ADMIN",  text: "ADMIN",     label: "ตั้งค่า",     to: "/settings", icon: Settings },
  { key: "ALERTS", text: "ALERTS",    label: "แจ้งเตือน",  to: "/inbox",    icon: Bell },
  { key: "FIELD",  text: "FIELD APP", label: "ภาคสนาม",    to: null,        icon: Radio },
];

const SLOT_ROUTES: ReadonlySet<string> =
  new Set(NAV_SLOTS.map((s) => s.to).filter((to): to is string => to !== null));

/** The NAV entry a slot names, or undefined if the slot has no destination.
 *  Nav-model invariants in AppShell.test.tsx fail if a slot names a route NAV
 *  does not declare, so this is undefined only for a `to: null` slot. */
function entryFor(slot: NavSlot): NavItem | undefined {
  return slot.to === null ? undefined : NAV.find((i) => i.to === slot.to);
}

/** The slots that render: they have a destination, and the viewer may use it.
 *  The staff gate is READ from NAV, never restated on the slot. */
export function visibleSlots(isStaff: boolean): (NavSlot & { to: string })[] {
  return NAV_SLOTS.filter((s): s is NavSlot & { to: string } => {
    if (s.to === null) return false;
    return isStaff || !entryFor(s)?.staffOnly;
  });
}

/** Destinations the seven slots have no slot for. They ride in the same nav
 *  row, after a divider.
 *
 *  This is now the ONLY surface for a destination without a slot — MenuBar
 *  was the other one and retired on 2026-09-01 — which is exactly why FILES
 *  could stop being a slot without becoming unreachable. Every entry here is
 *  a NAV destination no slot names: /today, /files/queue and /reports. */
export function promotedNav(isStaff: boolean): NavItem[] {
  return visibleQuickRow(isStaff).filter((i) => !SLOT_ROUTES.has(i.to));
}
